import { useEffect, useState } from 'react';
import { httpsCallable } from '../../lib/callable';
import { updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  Loader2,
  ShieldCheck,
  Mail,
  Clock,
  X,
  Trash2,
  ArrowRightLeft,
  MoreVertical,
  Check,
  Lock,
} from 'lucide-react';
import {
  useProjectMembers,
  useProjectInvitations,
  useProjectMembership,
} from '../../hooks/useProjectMembership';
import {
  AccessRole,
  ACCESS_ROLE_DESCRIPTIONS,
  ACCESS_ROLE_LABELS,
  Privilege,
  ProjectMemberDoc,
  memberHasPrivilege,
  roleDefaultGrants,
} from '../../lib/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { projectDoc } from '../../constants/firestorePaths';
import { useApprovalWorkflowEnabled } from '../../hooks/useApprovalWorkflowEnabled';
import { useProjectClient } from '../../hooks/useProjectClient';
import InviteMemberModal from './InviteMemberModal';

interface MembersPanelProps {
  projectId: string;
}

// Columns shown in the privilege grid. The order matches the column
// header row. "View" isn't a real privilege — every project member can
// read — so it's a static locked column rather than a toggle.
const PRIVILEGE_COLUMNS: Array<{
  key: Privilege;
  label: string;
  short: string;
}> = [
  { key: 'download', label: 'Download / export', short: 'Download' },
  { key: 'edit', label: 'Edit content', short: 'Edit' },
  { key: 'generate', label: 'Generate / regenerate', short: 'Generate' },
  { key: 'upload', label: 'Upload protocols / packets', short: 'Upload' },
  { key: 'approve', label: 'Approve artifacts', short: 'Approve' },
  { key: 'issue_documents', label: 'Approve and issue document versions', short: 'Issue' },
  { key: 'view_activity', label: 'View activity log', short: 'Activity' },
];

const ROLE_BADGE_CLASS: Record<AccessRole, string> = {
  owner: 'bg-amber-50 text-amber-800 ring-amber-200',
  manager: 'bg-blue-50 text-blue-800 ring-blue-200',
  editor: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  reviewer: 'bg-violet-50 text-violet-800 ring-violet-200',
  viewer: 'bg-slate-50 text-slate-700 ring-slate-200',
};

const CUSTOM_BADGE_CLASS = 'bg-purple-50 text-purple-800 ring-purple-200';

const ROLE_DROPDOWN_OPTIONS: AccessRole[] = [
  'manager',
  'editor',
  'reviewer',
  'viewer',
];

function formatDate(value?: { toDate?: () => Date } | Date): string {
  if (!value) return '';
  const date =
    value instanceof Date
      ? value
      : typeof (value as { toDate?: () => Date }).toDate === 'function'
      ? (value as { toDate: () => Date }).toDate()
      : null;
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function hasAnyOverrides(member: ProjectMemberDoc): boolean {
  if (!member.permissionOverrides) return false;
  // An override only counts if it actually flips the role default.
  for (const [k, v] of Object.entries(member.permissionOverrides)) {
    if (typeof v !== 'boolean') continue;
    const def = roleDefaultGrants(member.accessRole, k as Privilege);
    if (v !== def) return true;
  }
  return false;
}

export default function MembersPanel({ projectId }: MembersPanelProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { members, loading: membersLoading } = useProjectMembers(projectId);
  const { invitations, loading: invitesLoading } = useProjectInvitations(projectId);
  const { has, role: myRole, loading: meLoading } = useProjectMembership(projectId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [busyMemberUid, setBusyMemberUid] = useState<string | null>(null);
  const [openMenuUid, setOpenMenuUid] = useState<string | null>(null);

  const canManage = has('manage_members');
  const isOwner = myRole === 'owner';
  const loading = membersLoading || invitesLoading || meLoading;

  // Opt-in approval-workflow project setting (off by default). Writing the
  // project doc directly is gated to owner/manager by firestore.rules.
  const { enabled: approvalEnabled, loading: approvalLoading } =
    useApprovalWorkflowEnabled(projectId);
  const [approvalSaving, setApprovalSaving] = useState(false);

  const handleToggleApprovalWorkflow = async () => {
    if (!canManage || approvalSaving) return;
    const next = !approvalEnabled;
    setApprovalSaving(true);
    try {
      await updateDoc(projectDoc(projectId), {
        approvalWorkflowEnabled: next,
        updatedAt: serverTimestamp(),
      });
      showToast(
        next ? 'Approval workflow enabled' : 'Approval workflow disabled',
        'success'
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Could not update setting.';
      showToast(msg, 'error');
    } finally {
      setApprovalSaving(false);
    }
  };

  // Client / sponsor organization — an administrative label. It no longer
  // grants access to anything: cross-project instruction serving is closed, so
  // two projects sharing a client name pool nothing.
  // Saved on blur; writing the project doc directly is gated to
  // owner/manager by firestore.rules, same as the approval toggle — which is
  // precisely why this value cannot be a tenancy boundary: the customer sets it.
  const { client: storedClient, loading: clientLoading } =
    useProjectClient(projectId);
  const [clientName, setClientName] = useState('');
  const [clientSaving, setClientSaving] = useState(false);

  // Keep the input in sync with the stored value (initial load, the echo
  // of our own save, or an edit by another member).
  useEffect(() => {
    setClientName(storedClient ?? '');
  }, [storedClient]);

  const handleSaveClient = async () => {
    if (!canManage || clientSaving) return;
    const trimmed = clientName.trim();
    if (trimmed !== clientName) setClientName(trimmed);
    if (trimmed === (storedClient ?? '')) return; // unchanged — skip write
    setClientSaving(true);
    try {
      await updateDoc(projectDoc(projectId), {
        client: trimmed || null,
        updatedAt: serverTimestamp(),
      });
      showToast('Client saved', 'success');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Could not save client.';
      showToast(msg, 'error');
    } finally {
      setClientSaving(false);
    }
  };

  const handleRevoke = async (token: string, email: string) => {
    if (!confirm(`Revoke invitation for ${email}?`)) return;
    setRevokingToken(token);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.REVOKE_INVITATION, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await fn({ projectId, token });
      showToast('Invitation revoked', 'success');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Could not revoke invitation.';
      showToast(msg, 'error');
    } finally {
      setRevokingToken(null);
    }
  };

  const handleRoleChange = async (memberUid: string, accessRole: AccessRole) => {
    setBusyMemberUid(memberUid);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_MEMBER, { timeout: CALLABLE_TIMEOUTS.SHORT });
      // Picking a base role clears any overrides — the member's
      // privileges revert to the role's defaults. The "Custom" pill
      // disappears.
      await fn({ projectId, memberUid, accessRole, permissionOverrides: {} });
      showToast('Role updated', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not update role.';
      showToast(msg, 'error');
    } finally {
      setBusyMemberUid(null);
    }
  };

  /**
   * Toggle a single privilege on a member. If the new value matches the
   * role default, we remove the override (cleanup); otherwise we record
   * an explicit override on top of the base role.
   */
  const handlePrivilegeToggle = async (
    member: ProjectMemberDoc,
    privilege: Privilege,
    nextValue: boolean
  ) => {
    setBusyMemberUid(member.userId);
    try {
      const overrides: Record<string, boolean> = {
        ...(member.permissionOverrides || {}),
      };
      const def = roleDefaultGrants(member.accessRole, privilege);
      if (nextValue === def) {
        delete overrides[privilege];
      } else {
        overrides[privilege] = nextValue;
      }
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_MEMBER, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await fn({
        projectId,
        memberUid: member.userId,
        permissionOverrides: overrides,
      });
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Could not update privilege.';
      showToast(msg, 'error');
    } finally {
      setBusyMemberUid(null);
    }
  };

  const handleRemove = async (memberUid: string, label: string) => {
    setOpenMenuUid(null);
    if (!confirm(`Remove ${label} from this project?`)) return;
    setBusyMemberUid(memberUid);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.REMOVE_MEMBER, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await fn({ projectId, memberUid });
      showToast(`${label} removed`, 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not remove member.';
      showToast(msg, 'error');
    } finally {
      setBusyMemberUid(null);
    }
  };

  const handleTransferOwnership = async (memberUid: string, label: string) => {
    setOpenMenuUid(null);
    const ok = confirm(
      `Transfer project ownership to ${label}?\n\n` +
        `You will become a Manager. This cannot be undone except by the new ` +
        `Owner transferring it back.`
    );
    if (!ok) return;
    setBusyMemberUid(memberUid);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.TRANSFER_OWNERSHIP, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await fn({ projectId, newOwnerUid: memberUid });
      showToast(`Ownership transferred to ${label}`, 'success');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Could not transfer ownership.';
      showToast(msg, 'error');
    } finally {
      setBusyMemberUid(null);
    }
  };

  return (
    <div className="max-w-6xl">
      <header className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Team</h1>
            <p className="mt-1 text-sm text-gray-500">
              People with access to this project. Toggle individual
              privileges to grant a custom mix of access on top of the
              base role.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Invite member
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          <section className="mb-10">
            <div className="rounded-xl ring-1 ring-gray-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/60">
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-gray-500">
                      Account
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-gray-500">
                      Role
                    </th>
                    <th className="text-center px-2 py-3 font-semibold text-xs uppercase tracking-wide text-gray-500">
                      View
                    </th>
                    {PRIVILEGE_COLUMNS.map(col => (
                      <th
                        key={col.key}
                        className="text-center px-2 py-3 font-semibold text-xs uppercase tracking-wide text-gray-500"
                        title={col.label}
                      >
                        {col.short}
                      </th>
                    ))}
                    <th className="px-2 py-3 w-10" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {members.map(member => {
                    const isMe = user?.uid === member.userId;
                    const display =
                      member.displayName || member.email || member.userId;
                    const isOwnerRow = member.accessRole === 'owner';
                    const busy = busyMemberUid === member.userId;
                    const customised = hasAnyOverrides(member);
                    const canEditRow = canManage && !isOwnerRow && !isMe;
                    const showActionsMenu = !isOwnerRow && !isMe && (canManage || isOwner);

                    return (
                      <tr key={member.userId} className="hover:bg-gray-50/40">
                        {/* Account */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 truncate">
                              {display}
                            </p>
                            {isMe && (
                              <span className="text-[11px] font-medium text-gray-500">
                                (you)
                              </span>
                            )}
                          </div>
                          {member.email && member.email !== display && (
                            <p className="text-xs text-gray-500 truncate">
                              {member.email}
                            </p>
                          )}
                        </td>

                        {/* Role */}
                        <td className="px-4 py-3 align-top">
                          {isOwnerRow ? (
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${ROLE_BADGE_CLASS.owner}`}
                              title={ACCESS_ROLE_DESCRIPTIONS.owner}
                            >
                              <ShieldCheck className="h-3 w-3" />
                              Owner
                            </span>
                          ) : canEditRow ? (
                            <div className="flex flex-col gap-1">
                              <select
                                value={member.accessRole}
                                disabled={busy}
                                onChange={e =>
                                  handleRoleChange(
                                    member.userId,
                                    e.target.value as AccessRole
                                  )
                                }
                                className={`text-xs font-medium rounded-full px-2.5 py-1 ring-1 ring-inset cursor-pointer disabled:opacity-50 ${
                                  customised
                                    ? CUSTOM_BADGE_CLASS
                                    : ROLE_BADGE_CLASS[member.accessRole]
                                }`}
                                title={
                                  customised
                                    ? `Custom — based on ${ACCESS_ROLE_LABELS[member.accessRole]}. Pick a role to reset.`
                                    : ACCESS_ROLE_DESCRIPTIONS[member.accessRole]
                                }
                              >
                                {ROLE_DROPDOWN_OPTIONS.map(role => (
                                  <option key={role} value={role}>
                                    {ACCESS_ROLE_LABELS[role]}
                                  </option>
                                ))}
                              </select>
                              {customised && (
                                <span className="text-[10px] font-medium text-purple-700 px-1">
                                  Custom
                                </span>
                              )}
                            </div>
                          ) : (
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                                customised
                                  ? CUSTOM_BADGE_CLASS
                                  : ROLE_BADGE_CLASS[member.accessRole]
                              }`}
                              title={
                                customised
                                  ? `Custom — based on ${ACCESS_ROLE_LABELS[member.accessRole]}.`
                                  : ACCESS_ROLE_DESCRIPTIONS[member.accessRole]
                              }
                            >
                              {customised
                                ? 'Custom'
                                : ACCESS_ROLE_LABELS[member.accessRole]}
                            </span>
                          )}
                        </td>

                        {/* View — implicit and locked. Membership is the
                            view gate; remove the row to revoke. */}
                        <td className="px-2 py-3 align-top text-center">
                          <span
                            className="inline-flex items-center justify-center h-5 w-5 rounded text-emerald-600"
                            title="Every project member can view this project. Remove the member to revoke."
                          >
                            <Check className="h-4 w-4" />
                          </span>
                        </td>

                        {/* Privilege checkboxes */}
                        {PRIVILEGE_COLUMNS.map(col => {
                          const checked = memberHasPrivilege(member, col.key);
                          const overridden =
                            member.permissionOverrides?.[col.key] !== undefined;
                          const editable = canEditRow && !isOwnerRow;
                          return (
                            <td
                              key={col.key}
                              className="px-2 py-3 align-top text-center"
                            >
                              <label
                                className={`relative inline-flex items-center justify-center h-5 w-5 rounded ${
                                  editable
                                    ? 'cursor-pointer'
                                    : 'cursor-not-allowed'
                                }`}
                                title={
                                  isOwnerRow
                                    ? 'Owners always have every privilege.'
                                    : !editable
                                    ? `${col.label} — ${
                                        checked ? 'allowed' : 'not allowed'
                                      }`
                                    : `${col.label} — click to ${
                                        checked ? 'revoke' : 'grant'
                                      }${overridden ? ' (currently a custom override)' : ''}`
                                }
                              >
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={checked}
                                  disabled={!editable || busy}
                                  onChange={() =>
                                    handlePrivilegeToggle(
                                      member,
                                      col.key,
                                      !checked
                                    )
                                  }
                                />
                                <span
                                  className={`h-5 w-5 rounded ring-1 ring-inset flex items-center justify-center transition-colors ${
                                    checked
                                      ? overridden
                                        ? 'bg-purple-100 ring-purple-300 text-purple-700'
                                        : 'bg-emerald-50 ring-emerald-300 text-emerald-600'
                                      : 'bg-white ring-gray-300 text-gray-300'
                                  } ${!editable ? 'opacity-80' : ''}`}
                                >
                                  {checked && <Check className="h-4 w-4" />}
                                  {!editable && isOwnerRow && (
                                    <Lock className="absolute -bottom-1 -right-1 h-2.5 w-2.5 text-amber-600" />
                                  )}
                                </span>
                              </label>
                            </td>
                          );
                        })}

                        {/* Actions */}
                        <td className="px-2 py-3 align-top text-right">
                          {busy ? (
                            <Loader2 className="inline h-4 w-4 animate-spin text-gray-400" />
                          ) : showActionsMenu ? (
                            <div className="relative inline-block">
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenMenuUid(
                                    openMenuUid === member.userId
                                      ? null
                                      : member.userId
                                  )
                                }
                                className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                title="Member actions"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </button>
                              {openMenuUid === member.userId && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setOpenMenuUid(null)}
                                  />
                                  <div className="absolute right-0 top-full mt-1 z-20 w-56 rounded-lg ring-1 ring-gray-200 bg-white shadow-lg overflow-hidden">
                                    {isOwner && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleTransferOwnership(
                                            member.userId,
                                            display
                                          )
                                        }
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <ArrowRightLeft className="h-4 w-4 text-gray-500" />
                                        Transfer ownership to&nbsp;them
                                      </button>
                                    )}
                                    {canManage && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleRemove(member.userId, display)
                                        }
                                        className="w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Remove from project
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-xs text-gray-500 max-w-3xl">
              Pick a role from the dropdown to apply that role's default
              privileges. Then click any privilege checkbox to add a
              per-member exception — purple cells show overrides on top
              of the base role, and the role label switches to{' '}
              <span className="font-medium text-purple-700">Custom</span>.
              Pick a role again to clear overrides.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
              Project settings
            </h2>
            <div className="rounded-xl ring-1 ring-gray-200 bg-white p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">
                  Approval workflow
                </h3>
                <p className="text-sm text-gray-500 mt-0.5 max-w-xl">
                  Require formal review and approval before artifacts are
                  finalized. Off by default. When on, Submit for review and
                  Approve / Request changes / Reject actions appear on each
                  artifact for members with the matching privileges.
                </p>
                {!canManage && (
                  <p className="text-xs text-gray-400 mt-1.5">
                    Only owners and managers can change this setting.
                  </p>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={approvalEnabled}
                aria-label="Approval workflow"
                disabled={!canManage || approvalSaving || approvalLoading}
                onClick={handleToggleApprovalWorkflow}
                title={
                  canManage
                    ? 'Toggle the approval workflow for this project'
                    : 'Only owners and managers can change this setting'
                }
                className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  approvalEnabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    approvalEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Client / sponsor org — an administrative label only. It used to
                pool CRF completion instructions across projects sharing the
                name; that serving path was closed (see CROSS_PROJECT_SERVING_ENABLED
                in functions/src/crfGuide/instructionLibrary.ts), partly BECAUSE
                this box is customer-writable and so could never be a tenancy
                boundary. The copy must not invite matching spellings. */}
            <div className="mt-4 rounded-xl ring-1 ring-gray-200 bg-white p-5">
              <label
                htmlFor="project-client"
                className="block text-sm font-semibold text-gray-900"
              >
                Client
              </label>
              <p className="text-sm text-gray-500 mt-0.5 max-w-xl">
                The sponsor or client organization this project belongs to, used
                for labelling and reporting. It does not affect what this project
                can access — no study content is shared between projects.
              </p>
              {!canManage && (
                <p className="text-xs text-gray-400 mt-1.5">
                  Only owners and managers can change this setting.
                </p>
              )}
              <input
                id="project-client"
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                onBlur={handleSaveClient}
                placeholder="e.g. Acme Pharma"
                disabled={!canManage || clientSaving || clientLoading}
                className="mt-3 w-full max-w-sm rounded-lg ring-1 ring-gray-200 focus:ring-primary-500 focus:ring-2 px-3 py-2 text-sm outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </section>

          {invitations.length > 0 && (
            <section className="mb-10">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
                Pending invitations ({invitations.length})
              </h2>
              <ul className="divide-y divide-gray-100 rounded-xl ring-1 ring-gray-200 bg-white overflow-hidden">
                {invitations.map(invite => (
                  <li
                    key={invite.token}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {invite.invitedEmail}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Invited {formatDate(invite.invitedAt)}
                        {invite.invitedByName ? ` by ${invite.invitedByName}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                          ROLE_BADGE_CLASS[invite.accessRole]
                        }`}
                      >
                        {ACCESS_ROLE_LABELS[invite.accessRole]}
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          onClick={() =>
                            handleRevoke(invite.token, invite.invitedEmail)
                          }
                          disabled={revokingToken === invite.token}
                          title="Revoke invitation"
                          className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                        >
                          {revokingToken === invite.token ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-xl bg-gray-50 ring-1 ring-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              About roles
            </h3>
            <dl className="space-y-2 text-sm">
              {(Object.keys(ACCESS_ROLE_LABELS) as AccessRole[]).map(role => (
                <div key={role} className="flex items-start gap-3">
                  <dt
                    className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset shrink-0 ${ROLE_BADGE_CLASS[role]}`}
                  >
                    {ACCESS_ROLE_LABELS[role]}
                  </dt>
                  <dd className="text-gray-600">
                    {ACCESS_ROLE_DESCRIPTIONS[role]}
                  </dd>
                </div>
              ))}
              <div className="flex items-start gap-3">
                <dt
                  className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset shrink-0 ${CUSTOM_BADGE_CLASS}`}
                >
                  Custom
                </dt>
                <dd className="text-gray-600">
                  Any base role with one or more privilege overrides
                  applied. Useful when a single member needs a slightly
                  different mix than the role's defaults provide.
                </dd>
              </div>
            </dl>
          </section>
        </>
      )}

      {inviteOpen && (
        <InviteMemberModal
          projectId={projectId}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}

